import { CurvoErrorCode } from '../gen/models'

/**
 * Copy for the two framework-level (Apollo) validation codes below. Intentionally generic: these
 * codes carry no user-actionable detail, and the server masks the raw message in production
 * (`graphql-config.service.ts` rewrites GRAPHQL_VALIDATION_FAILED to 'Invalid Request').
 */
const REQUEST_INVALID_COPY =
  'Something went wrong with your request. Please try again or contact our support to help you fix it.'

export const ERROR_MESSAGE_MAP: Record<CurvoErrorCode | string, string> = {
  INTERNAL_SERVER_ERROR: 'Something went wrong. Please contact our support to help you fix it.',
  // Framework-level (Apollo) codes, emitted BEFORE any resolver runs — input coercion and document
  // validation respectively. No CurvoError carries them (`CurvoErrorCode` is only
  // INTERNAL_SERVER_ERROR / PERMISSION_ERROR / VALIDATION_ERROR), so they always mean the request
  // never executed: a client/server contract bug, never an expected flow. Previously unmapped, which
  // made those failures silent (#7636). Deliberately NOT a catch-all fallback for unmapped codes —
  // `FORBIDDEN` guard denials are required to stay silent (errorHandler.test.ts E4).
  BAD_USER_INPUT: REQUEST_INVALID_COPY,
  GRAPHQL_VALIDATION_FAILED: REQUEST_INVALID_COPY,
  QUICK_CREATE_ORG_DUP:
    'A Facility with that name already exists. Please select the existing one or enter a unique name to proceed.',
  EXPORT_CATALOG_PARTS_ERROR: 'The export failed. Please try again or contact our support to help you fix it.',
  TIER_CONTRACT_UPDATE_FORBIDDEN: 'You do not have permission to update contract tiers.',
  TIER_CONTRACT_CREATE_FORBIDDEN: 'You do not have permission to create contract tiers.',
  TIER_CONTRACT_RULES_FORBIDDEN: 'You do not have permission to update contract tier rules.',
  OPPORTUNITY_UPLOAD_FORBIDDEN: 'You do not have permission to upload opportunities.',
  VENDOR_USERS_LOGGED_IN_USERNAME_FORBIDDEN: 'You do not have permission to look up vendor users this way.',
  OFFER_TIER_REBATE_READ_FORBIDDEN: 'You do not have permission to view this rebate.',
  OFFER_REBATE_REMOVE_FORBIDDEN: 'You do not have permission to delete this rebate.',
  CATALOG_RESET_BLOCKED: 'Unable to empty the catalog as its parts are tied to an opportunity.',
  // #7880 — ONE code for four offer-keyed supplier writes (tiers, cross-list re-apply, create new offer,
  // cross/linkage saves): the condition is about the opportunity, not the action, so the copy is shared.
  SUPPLIER_OPPORTUNITY_WRITE_FORBIDDEN: 'You do not have permission to change this opportunity.',
  // #8282 — the Update Catalog Item save's server-side rules; the copy equals the server message.
  CATALOG_ITEM_UOM_REQUIRED: 'UoM is required for every price tier.',
  CATALOG_ITEM_UOM_CONVERSION_NOT_POSITIVE: 'UoM conversion must be greater than 0 for every price tier.',
  // Equals the form's PRICE_NOT_POSITIVE_MESSAGE (7327) and the server message; catalogItemErrorMessages.test locks it.
  CATALOG_ITEM_PRICE_NOT_POSITIVE: "Price cannot be negative or '0'.",
}

/**
 * Copy keyed by the server's error MESSAGE, consulted BEFORE `ERROR_MESSAGE_MAP` in
 * `extractErrorMessages` and matched EXACTLY (#7700). It exists because the failure below reaches the
 * client under the app-wide generic code `INTERNAL_SERVER_ERROR`, which cannot distinguish it from every
 * other internal error — the message can. Kept as a SEPARATE map so the code and message key namespaces
 * cannot collide and `ERROR_MESSAGE_MAP` above stays untouched. Like that map, deliberately NOT a
 * catch-all: an unmatched message simply falls through to the code lookup, which keeps `FORBIDDEN` guard
 * denials and unmapped codes silent (errorHandler.test.ts E4/E7).
 *
 * The two entries are AUTHORED improvements, not Appian ports: Appian shows nothing at all on either
 * failure (#7700 discovery E6; #7854 discovery §2.4 — the User Access dialog closes and a failure task is
 * parked out of band). The #7700 copy is stakeholder-locked and ships verbatim — it deliberately
 * diverges from this file's one-sentence house style, because it must stay true both when the user picked
 * a colliding cross item (user-fixable) and when the row is unsaveable whatever the user does (#7700 E1).
 * The #7854 copy is the ticket's AC-7854-1 wording.
 *
 * ⚠️ TWO ACCEPTED COUPLINGS per entry (discovery §Q10.3 — accepted with eyes open, not mitigated; the
 * #7854 entry carries the same two, keyed on `offer.service.ts`'s owner-only throw in
 * `updateSupplierOpportunityACLs` — single thrower, single operation `updateSupplierOpportunityACL`, single
 * call site `UserAccessDialog.tsx`):
 * (i)  MESSAGE-STRING: the key is the backend's literal throw at
 *      `server/src/modules/CrossReference/services/cross-reference-map.service.ts:205`. A backend
 *      rewording silently stops this entry firing and degrades the toast back to the generic
 *      `INTERNAL_SERVER_ERROR` copy above — graceful degradation, not breakage.
 * (ii) VOCABULARY: Crosses-specific wording ("in this list", "cross item") lives in an app-wide map.
 *      Nothing else can reach it today (single server thrower, single operation
 *      `saveCrossReferenceMappings`, single call site `CrossesEditDialog.tsx`), but any future surface
 *      hitting the same throw would display Crosses vocabulary.
 */
export const SERVER_MESSAGE_ERROR_MAP: Record<string, string> = {
  'Product cannot be on both sides of mappings':
    "This cross can't be saved because one of these products is already used on the other side of a mapping in this list. If you selected a cross item, try a different one; if not, contact support.",
  'Only Supplier Opportunity owner can update acl': 'Only the opportunity owner can change user access.',
}
